import React, { useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { useEntitlements } from '../hooks/useEntitlements';
import { useCompanyDetails } from '../pages/Schedule/v6/useScheduleV6Gate';
import HoldDialog from '../pages/Schedule/v6/HoldDialog';
import { fetchHoldsPreview } from '../queries/scheduleRules';
import '../pages/Schedule/v6/schedule.css';

/**
 * Every place the web changes what a screen shows BY HAND (the Screens page, «دفع محتوى», the Sources «Change Campaign» picker,
 * Café Vibes) asks the backend first which of the target screens are inside a schedule window right now
 * (`GET /campaign-schedules/holds/preview`). Nothing playing = nothing happens (the call goes straight on, as before).
 * Something playing = the SAME dialog the schedule page uses (HoldDialog) says what will pause and until when; "continue"
 * sends the change (the backend then creates the hold), "cancel" sends nothing.
 *
 * A preview that fails never blocks a change. Restaurant accounts are never asked.
 */
export const holdCancelled = () => Object.assign(new Error('schedule hold dialog cancelled'), { holdCancelled: true });

/** Open the dialog over everything and resolve true (continue) / false (cancel). */
export function confirmHold({ screens, total, tz }) {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    host.style.cssText = 'position:relative;z-index:3000';
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = (ok) => { root.unmount(); host.remove(); resolve(ok); };
    root.render(
      <div className="sch6" dir={document.documentElement.getAttribute('dir') || 'rtl'} style={{ display: 'contents' }}>
        <HoldDialog screens={screens} total={total} tz={tz} onConfirm={() => done(true)} onClose={() => done(false)} />
      </div>,
    );
  });
}

/** Resolve when the change may go on; reject with `holdCancelled()` when the person cancelled the dialog. */
export async function ensureHoldConfirmed(where, { tz, total } = {}) {
  if (!where || (where.mode !== 'all' && !(where.ids || []).length)) return;
  let res;
  try { res = await fetchHoldsPreview(where); } catch (_) { return; }
  const live = (res?.screens || []).filter((s) => !s.already_paused);
  if (!live.length) return;
  const ok = await confirmHold({ screens: live, total: Math.max(total || 0, live.length), tz: tz || 'Asia/Riyadh' });
  if (!ok) throw holdCancelled();
}

/** `ensure(where)` for a mutation function. */
export function useHoldGuard() {
  const { ready, isRestaurant } = useEntitlements();
  const details = useCompanyDetails();
  const tz = details?.company?.timezone;
  const total = details?.total_used_devices;
  return useCallback((where) => ((!ready || isRestaurant) ? Promise.resolve() : ensureHoldConfirmed(where, { tz, total })), [ready, isRestaurant, tz, total]);
}

/** Where a `/campaign/assign/devices` payload points. */
export function whereOfAssign(payload) {
  if (payload?.type === 'groups') return { mode: 'groups', ids: payload.groups_ids || [] };
  if (payload?.select_all) return { mode: 'all' };
  return { mode: 'screens', ids: payload?.devices_ids || [] };
}
